import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPriceCompliance } from '@/api/clarityLens'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { PriceComplianceModel } from '@/types/clarityLens'

type BarChartDataModel = {
  data: { name: string; value: number }[]
}

export default function usePriceCompliance(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [priceComplianceChartData, setPriceComplianceChartData] =
    useState<BarChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const response = await getPriceCompliance(
        branches,
        startDate,
        endDate,
        itemName
      )

      setPriceComplianceChartData(convertToBarChart(response))
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sku breakdown information...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    priceComplianceChartData,
    loading,
    refetch,
  }
}

function convertToBarChart(
  priceCompData: PriceComplianceModel
): BarChartDataModel {
  const data = Object.entries(priceCompData).map(([name, value]) => ({
    name,
    value,
  }))

  return {
    data,
  }
}
